import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Section, Eyebrow, Heading, Lede } from "../components/site/Section";
import Placeholder from "../components/site/Placeholder";
import SubscribeForm from "../components/site/SubscribeForm";
import { ART, NATIONS, CHARACTERS, SITE, SEASONS, SAGA_POSTERS } from "../lib/content";

export default function HomePage() {
  const s1Nations = NATIONS.filter((n) => n.season === "Season One");
  return (
    <div data-testid="home-page">
      {/* HERO — uses the deck's signature trio plate as the backdrop */}
      <section className="relative" data-testid="hero-section">
        <div className="absolute inset-0 -z-10">
          <img
            src={ART.hero}
            alt="Chains Beneath Bakhtak — the trio"
            className="w-full h-full object-cover opacity-90 md:opacity-95"
            style={ART.heroPosition ? { objectPosition: ART.heroPosition } : undefined}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/30 via-[#050505]/50 to-[#050505]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/70 via-transparent to-[#050505]/50" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-36 pb-24 md:pb-44 min-h-[92vh] md:min-h-[100vh] flex flex-col justify-end">
          <Eyebrow testId="hero-eyebrow">Break the Illusion</Eyebrow>
          <h1
            data-testid="hero-title"
            className="font-display text-5xl sm:text-6xl md:text-8xl text-[#EAE0D5] leading-[0.95] mt-4 max-w-4xl drop-shadow-[0_4px_20px_rgba(0,0,0,0.9)]"
          >
            Chains Beneath
            <br />
            <span className="text-[#C5A059] italic font-light">Bakhtak</span>
          </h1>
          <p
            data-testid="hero-tagline"
            className="mt-6 max-w-2xl text-base md:text-lg text-[#EAE0D5] leading-relaxed drop-shadow-[0_2px_10px_rgba(0,0,0,0.9)]"
          >
            {SITE.shortPitch}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-3">
            <Link
              to="/pilot"
              data-testid="hero-read-pilot"
              className="bg-[#C5A059] text-black px-7 py-3.5 text-xs tracking-[0.22em] uppercase hover:bg-[#D4AF68] transition-colors inline-flex items-center justify-center gap-2"
            >
              Read the Pilot <ArrowRight size={14} />
            </Link>
            <Link
              to="/nation-quiz"
              data-testid="hero-take-quiz"
              className="bg-black/60 backdrop-blur-sm border border-[#C5A059] text-[#C5A059] px-7 py-3.5 text-xs tracking-[0.22em] uppercase hover:bg-[#C5A059] hover:text-black transition-colors inline-flex items-center justify-center"
            >
              Take the Nation Quiz
            </Link>
            <Link
              to="/community"
              data-testid="hero-join"
              className="border border-[#C5A059]/60 text-[#C5A059] bg-black/30 backdrop-blur-sm px-7 py-3.5 text-xs tracking-[0.22em] uppercase hover:bg-[#C5A059]/10 transition-colors inline-flex items-center justify-center"
            >
              Join the Community
            </Link>
          </div>
        </div>
      </section>

      {/* PREMISE STRIP */}
      <Section testId="intro-section" className="border-t border-white/5">
        <div className="grid md:grid-cols-12 gap-8 md:gap-16">
          <div className="md:col-span-5">
            <Eyebrow>The World</Eyebrow>
            <Heading>Peace as performance.</Heading>
          </div>
          <div className="md:col-span-7 text-[#A39E99] leading-relaxed space-y-5 text-base md:text-lg">
            <p>{SITE.longPitch}</p>
            <p>
              Verdani hides in honesty. Specula polishes itself into heroism while dying of fading.
              Arathia celebrates perfection while its champions break behind closed doors.
              Namtaria smiles through ritual while its soul arts quietly destroy their own.
              <span className="text-[#EAE0D5]"> Theraka glorifies freedom and strength</span>, yet too often mistakes survival for purpose.
            </p>
            <p className="text-[#737373] text-sm pt-4">
              Season One begins in the quiet Verdani woods — where three wanderers stumble into a truth the rest of Bakhtak is determined to bury.
            </p>
          </div>
        </div>
      </Section>

      {/* SEASON ONE NATIONS PREVIEW */}
      <Section testId="nations-preview" className="border-t border-white/5">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-16">
          <div>
            <Eyebrow>The Nine Nations · Season One</Eyebrow>
            <Heading>Each tongue, a different mask.</Heading>
          </div>
          <Link
            to="/nations"
            className="link-gold text-sm tracking-[0.2em] uppercase self-start md:self-end"
            data-testid="home-nations-link"
          >
            See all nine →
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {s1Nations.map((n) => (
            <Link
              key={n.slug}
              to="/nations"
              data-testid={`home-nation-${n.slug}`}
              className="group block bg-[#0A0A0A] border border-white/5 hover:border-[#C5A059]/30 transition-all duration-500"
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = `0 0 48px -20px ${n.accent}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "0 0 0 transparent";
              }}
            >
              <Placeholder
                src={n.image}
                ratio="aspect-[4/5]"
                label={`${n.name} — ${n.epithet}`}
                alt={`${n.name} visual`}
                objectPosition={n.imageObjectPosition}
              />
              <div className="p-6">
                <div
                  className="text-[10px] tracking-[0.3em] uppercase"
                  style={{ color: n.accent }}
                >
                  {n.epithet}
                </div>
                <div className="font-display text-2xl mt-2 text-[#EAE0D5]">
                  {n.name}
                </div>
                <div className="text-xs text-[#737373] mt-2">
                  Virtue: <span className="text-[#A39E99]">{n.virtue}</span>
                  {"   ·   "}
                  Flaw: <span className="text-[#A39E99]">{n.flaw}</span>
                </div>
                <p className="text-sm text-[#A39E99] mt-3 leading-relaxed line-clamp-4">
                  {n.summary}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      {/* THE TRIO */}
      <Section testId="trio-section" className="border-t border-white/5">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-16">
          <div>
            <Eyebrow>The Trio</Eyebrow>
            <Heading>Three wanderers who weren't supposed to matter.</Heading>
          </div>
          <Link
            to="/characters"
            className="link-gold text-sm tracking-[0.2em] uppercase self-start md:self-end"
            data-testid="home-characters-link"
          >
            Meet the cast →
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {CHARACTERS.slice(0, 3).map((c) => (
            <div
              key={c.slug}
              data-testid={`home-character-${c.slug}`}
              className="bg-[#0A0A0A] border border-white/5"
            >
              <Placeholder
                src={c.image}
                ratio="aspect-[3/4]"
                label={c.name}
                alt={`${c.name} portrait`}
              />
              <div className="p-6">
                <div
                  className="text-[10px] tracking-[0.28em] uppercase"
                  style={{ color: c.accent }}
                >
                  {c.epithet}
                </div>
                <div className="font-display text-2xl mt-2 text-[#EAE0D5]">
                  {c.name}
                </div>
                <div className="text-xs text-[#737373] mt-1">{c.nation}</div>
                <p className="italic text-[#A39E99] mt-3 leading-relaxed text-sm">
                  "{c.line}"
                </p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ART DIRECTION — owner-supplied key posters */}
      <Section testId="saga-posters" className="border-t border-white/5">
        <Eyebrow>Art Direction</Eyebrow>
        <Heading className="mb-10">Key visuals from the world.</Heading>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-5">
          {SAGA_POSTERS.map((p) => (
            <figure
              key={p.id}
              data-testid={`saga-poster-${p.id}`}
              className="bg-[#0A0A0A] border border-white/5 group hover:border-[#C5A059]/30 transition-all duration-500"
            >
              <Placeholder
                src={p.image}
                ratio="aspect-[2/3]"
                label={p.title}
                alt={p.title}
                objectPosition={p.objectPosition}
              />
              <figcaption className="px-4 py-3 text-[10px] tracking-[0.22em] uppercase text-[#A39E99] text-center border-t border-white/5">
                {p.title}
              </figcaption>
            </figure>
          ))}
        </div>
      </Section>

      {/* SEASON ROADMAP */}
      <Section testId="seasons-section" className="border-t border-white/5">
        <Eyebrow>Roadmap</Eyebrow>
        <Heading className="mb-10">A franchise built to grow.</Heading>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {SEASONS.map((s) => (
            <article
              key={s.n}
              data-testid={`season-${s.n}`}
              className="bg-[#0A0A0A] border border-white/5 p-7 flex flex-col"
            >
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#C5A059]">
                Season {s.n}
              </div>
              <h3 className="font-display text-2xl text-[#EAE0D5] mt-3">
                {s.title}
              </h3>
              <p className="text-sm text-[#A39E99] mt-3 leading-relaxed">
                {s.summary}
              </p>
            </article>
          ))}
        </div>
        <p className="text-xs text-[#737373] mt-8">
          Seasons 2–10 are already structured; Season One is the pitch-ready entry point.
        </p>
      </Section>

      {/* COMPARABLE TITLES */}
      <Section testId="comparables-section" className="border-t border-white/5">
        <Eyebrow>Comparable Titles</Eyebrow>
        <Heading className="mb-8">In the company of giants.</Heading>
        <div className="flex flex-wrap gap-3">
          {SITE.comparableTitles.map((t) => (
            <span
              key={t}
              className="text-[11px] tracking-[0.22em] uppercase px-4 py-2.5 border border-[#C5A059]/40 text-[#C5A059]"
            >
              {t}
            </span>
          ))}
        </div>
        <p className="text-[#A39E99] mt-6 max-w-3xl leading-relaxed">
          Mature fantasy animation — the political tension of Attack on Titan, the
          expansive worldbuilding of One Piece, and the atmospheric intensity of
          Castlevania.
        </p>
      </Section>

      {/* JOIN CTA */}
      <Section testId="home-cta-section" className="border-t border-white/5">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <Eyebrow>Join the Dream</Eyebrow>
            <Heading>Be there when the pilot opens.</Heading>
            <Lede>
              No spam. Only chapter alerts, behind-the-veil notes, and first-look
              art from the world of Bakhtak.
            </Lede>
          </div>
          <div className="md:col-span-5">
            <SubscribeForm source="home" testIdPrefix="home-subscribe" />
            <p className="text-xs text-[#737373] mt-3">
              By subscribing you agree to receive whispers from the realm.
            </p>
          </div>
        </div>
      </Section>
    </div>
  );
}
